import { useRouter } from "next/router";
import { useEffect, useRef } from "react";
import { type AnalyticsConfig, analyticsService, defaultSessionReplaySampleRate, extractAttributionTraits, hasAttributionParams, pickAttributionParams } from "@medbillai/app/lib/analytics";
import { authStorage } from "@medbillai/app/lib/storage";

// Although "useEffect" will only run the hook once per mount, hot reloading can
// cause us to mount multiple times, so track initialization separately to avoid
// error noise.
let initialized = false;

// Campaign traffic lands directly on the funnel, so sessions entering here
// always replay. A floor, not an override, so a higher ambient rate still wins.
const FUNNEL_REPLAY_SAMPLE_RATE = 1;
const FUNNEL_REPLAY_PATH_PREFIXES = ["/natera-intake"] as const;

// `pathname` is the entry path, undefined when there is no window — the SDK
// samples once at init, so the funnel floor is decided by where the session
// started, not where it goes next.
export function resolveSessionReplaySampleRate(envSampleRate: string | undefined, pathname: string | undefined): number {
  const parsed = envSampleRate != null ? parseFloat(envSampleRate) : Number.NaN;
  const ambient = Number.isNaN(parsed) ? defaultSessionReplaySampleRate : parsed;
  return pathname != null && FUNNEL_REPLAY_PATH_PREFIXES.some(prefix => pathname.startsWith(prefix)) ? Math.max(ambient, FUNNEL_REPLAY_SAMPLE_RATE) : ambient;
}
export function useAnalyticsService() {
  const {
    pathname,
    query,
    isReady
  } = useRouter();
  // Track if we've already captured attribution for this session
  const attributionCapturedRef = useRef(false);

  // Initialize analytics service on mount
  useEffect(() => {
    if (initialized) {
      return;
    }
    initialized = true;
    if (!process.env.NEXT_PUBLIC_RUDDERSTACK_WRITE_KEY || !process.env.NEXT_PUBLIC_RUDDERSTACK_DATA_PLANE_URL) {
      console.error("[Analytics] NEXT_PUBLIC_RUDDERSTACK_WRITE_KEY/_DATA_PLANE_URL missing — analytics disabled");
      return;
    }
    const config: AnalyticsConfig = {
      writeKey: process.env.NEXT_PUBLIC_RUDDERSTACK_WRITE_KEY,
      dataPlaneUrl: process.env.NEXT_PUBLIC_RUDDERSTACK_DATA_PLANE_URL,
      debug: process.env.NEXT_PUBLIC_RUDDERSTACK_DEBUG === "true",
      amplitudeApiKey: process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY,
      sessionReplay: {
        enabled: process.env.NEXT_PUBLIC_SESSION_REPLAY_ENABLED === "true",
        sampleRate: resolveSessionReplaySampleRate(process.env.NEXT_PUBLIC_SESSION_REPLAY_SAMPLE_RATE, typeof window !== "undefined" ? window.location.pathname : undefined)
      }
    };
    void analyticsService.initialize(config);
  }, []);

  // Track screen view and handle anonymousId and attribution whenever pathname or query changes
  useEffect(() => {
    // The pages router hydrates in two steps: the first render has query = {}
    // with isReady false, then query populates as a new object and re-runs
    // this effect. Tracking that first pass double-fired every entry page
    // view (once with an empty query) and made attribution capture depend on
    // hasAttributionParams({}) being false.
    if (!isReady) {
      return;
    }

    // Set anonymousId if it exists in URL query params
    if (query.anonymous_id && typeof query.anonymous_id === "string") {
      analyticsService.setAnonymousId(query.anonymous_id);
    }

    // Capture attribution parameters if present and not already captured this session
    // Only capture on first page load to avoid overwriting attribution on
    // internal navigation if parameters get corrupted
    if (!attributionCapturedRef.current && hasAttributionParams(query)) {
      const attributionTraits = extractAttributionTraits(query);
      analyticsService.identifyAnonymousUser(attributionTraits);
      // Not through AuthState, so logout never clears it: stash only while
      // signed out or the next account on this browser inherits it.
      void authStorage.get("isAuthenticated").then(flag => {
        if (flag !== "true") {
          void authStorage.set("pendingAttribution", JSON.stringify(attributionTraits));
        }
      });
      attributionCapturedRef.current = true;
    }

    // Track screen view if pathname exists
    if (pathname) {
      analyticsService.trackScreenView(pathname, pickAttributionParams(query));
    }
  }, [isReady, pathname, query]);
}